<template>
  <div class="create-source-wrapper">
    <div class="content-area">
      <!-- Page Header -->
      <div class="page-header">
        <div class="header-content">
          <div class="header-left">
            <v-btn
              icon
              variant="text"
              size="medium"
              class="back-btn"
              @click="goBack">
              <Icon name="arrow-left" variant="solid" size="20" color="default"/>
            </v-btn>
            <h1 class="page-title">
              Create Source
            </h1>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="main-content main-content--split-row">
        <!-- Left Panel -->
        <div class="left-panel">
          <div class="form-content">
            <!-- Collection -->
            <div class="form-group">
              <label class="form-label required">
                Collection
                <span class="required-asterisk">*</span>
              </label>
              <MankSelect
                v-model="formData.collection"
                :items="collections"
                :loading="collectionsLoading"
                placeholder="Select Collection"
                class="collection-select"
                :menu-props="{
                  maxHeight:300,
                  offsetY:true,
                  nudgeBottom:8,
                }"
                :error-messages="errors.collection"
                hide-details="auto"
                item-text="name"
                item-value="id">
                <template v-slot:append>
                  <Icon name="expand-down" variant="solid" color="muted"/>
                </template>
              </MankSelect>
            </div>

            <!-- Source Name -->
            <div class="form-group">
              <label class="form-label required">
                Source Name
                <span class="required-asterisk">*</span>
              </label>
              <MankTextField
                v-model="formData.sourceName"
                placeholder="Enter Source Name"
                class="form-input"
                :error-messages="errors.sourceName"
                hide-details="auto"/>
            </div>

            <!-- Description -->
            <div class="form-group">
              <label class="form-label">Description</label>
              <MankTextarea
                v-model="formData.description"
                placeholder="Enter Source Description"
                variant="outlined"
                :error-messages="errors.description"
                hide-details="auto"
                rows="4"
                class="description-textarea"/>
              <div class="helper-text" :class="{ 'error-text': formData.description.length > 300 }">
                {{ formData.description.length }}/300
              </div>
            </div>
          </div>
        </div>

        <!-- Right Panel -->
        <div class="right-panel">
          <div class="form-content">
            <!-- Document Upload Area (only show when document tab is selected) -->

            <!-- Source Type Toggle -->
            <div class="form-group">
              <div class="source-type-toggle">
                <v-btn
                  :class="['toggle-btn', { active: formData.sourceType === 'web' }]"
                  @click="formData.sourceType = 'web'">
                  Web
                </v-btn>
                <v-btn
                  :class="['toggle-btn', { active: formData.sourceType === 'document' }]"
                  @click="formData.sourceType = 'document'">
                  Document
                </v-btn>
              </div>
            </div>

            <!-- Web Source Fields -->
            <div v-if="formData.sourceType === 'web'" class="web-fields">
              <!-- Crawl Depth -->
              <div class="form-group">
                <label class="form-label">Crawl Depth</label>
                <p class="field-description">
                  Defines the number of link levels from the start URL that will be followed and included in the collection.
                </p>
                <div class="slider-group">
                  <v-slider
                    v-model="formData.crawlDepth"
                    :min="0"
                    :max="6"
                    :step="1"
                    color="primary"
                    class="form-slider"
                    hide-details/>
                  <div class="slider-value">
                    {{ formData.crawlDepth }}
                  </div>
                </div>
              </div>

              <!-- URL -->
              <div class="url-section">
                <div class="form-group">
                  <label class="form-label required">
                    URL
                    <span class="required-asterisk">*</span>
                  </label>
                  <MankTextField
                    v-model="formData.url"
                    placeholder="https://"
                    class="form-input"
                    :error-messages="errors.url"
                    hide-details="auto"/>
                </div>

                <!-- Add URL Button -->
                <div class="add-url-btn" @click="addUrl">
                  <Icon name="plus" variant="solid" size="20" color="default"/>
                  <span class="add-url-text">Add URL</span>
                </div>
              </div>

              <!-- URL List -->
              <div v-if="urlList.length > 0" class="url-list">
                <div
                  v-for="(urlItem, index) in urlList"
                  :key="urlItem.id"
                  class="url-item">
                  <div class="url-content">
                    <Icon name="plus" variant="solid" size="20" color="default"/>
                    <span class="url-text">{{ urlItem.url }}</span>
                  </div>
                  <button
                    class="remove-url-btn"
                    @click="removeUrl(index)"
                    type="button">
                    <Icon name="trash" variant="solid" size="20" color="default"/>
                  </button>
                </div>
              </div>
            </div>
            <div v-if="formData.sourceType === 'document'" class="document-upload-area">
              <div
                class="drag-drop-area"
                @dragover.prevent
                @drop.prevent="handleFileDrop"
                @click="triggerFileUpload">
                <!-- RAG Icon -->
                <div class="upload-icon">
                  <div class="icon-circle">
                    <Icon name="rag" variant="solid" size="24" color="primary"/>
                  </div>
                </div>

                <!-- Text Content -->
                <div class="upload-text">
                  <h3 class="upload-title">
                    Drag & Drop or Upload File
                  </h3>
                  <p class="upload-description">
                    txt, pdf, html, doc, docx, epub, md files accepted
                  </p>
                </div>

                <!-- Upload Button -->
                <v-btn
                  variant="outlined"
                  size="medium"
                  class="upload-btn mank-btn"
                  elevation="0">
                  <Icon name="upload" variant="solid" size="20" color="primary"/>
                  Upload File
                </v-btn>

                <!-- Hidden File Input -->
                <input
                  ref="fileInput"
                  type="file"
                  multiple
                  accept=".txt,.pdf,.html,.doc,.docx,.epub,.md"
                  style="display: none"
                  @change="handleFileSelect">
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer Buttons -->
      <div class="footer-buttons">
        <v-btn
          variant="text"
          size="medium"
          class="cancel-btn"
          elevation="0"
          @click="cancel">
          <Icon name="cross" variant="solid" size="20" color="default"/>
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          size="medium"
          class="create-btn"
          elevation="0"
          :loading="loading"
          :disabled="!isFormValid || loading"
          @click="createSource">
          <Icon name="plus" variant="solid" size="20" color="onPrimary"/>
          Create
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from '@/components/Icon.vue';
import CollectionService from '../services/CollectionService';
import RagService from '../services/RagService';

export default {
  name: 'CreateSource',
  components: { Icon },
  data() {
    return {
      loading: false,
      collectionsLoading: false,
      formData: {
        collection: null,
        sourceName: '',
        description: '',
        sourceType: 'web', // 'web' or 'document'
        crawlDepth: 1,
        url: '',
      },
      urlList: [],
      collections: [],
      uploadedFiles: [],
      errors: {
        collection: [],
        sourceName: [],
        description: [],
        url: [],
        files: [],
      },
    };
  },
  computed: {
    isFormValid() {
      return (
        this.formData.collection
        && this.formData.sourceName.trim()
        && ((this.formData.sourceType === 'web' && (this.formData.url.trim() || this.urlList.length > 0))
         || (this.formData.sourceType === 'document' && this.uploadedFiles.length > 0))
      );
    },
  },
  async mounted() {
    await this.fetchCollections();
    this.checkCollectionFromQuery();
  },
  methods: {
    async fetchCollections() {
      this.collectionsLoading = true;
      try {
        const response = await CollectionService.getAllCollections();
        this.collections = (response.data || response || []).map((collection) => ({
          id: collection.id,
          name: collection.name,
        }));
      } catch (error) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: 'Failed to load collections',
        });
        this.collections = [];
      } finally {
        this.collectionsLoading = false;
      }
    },

    checkCollectionFromQuery() {
      const collectionId = this.$route.query.collectionId;
      if (collectionId && this.collections.length > 0) {
        const collection = this.collections.find((c) => c.id === collectionId);
        if (collection) {
          this.formData.collection = collection.id;
        }
      }
    },

    validateForm() {
      this.errors = {
        collection: [],
        sourceName: [],
        description: [],
        url: [],
        files: [],
      };

      let isValid = true;

      // Collection validation
      if (!this.formData.collection) {
        this.errors.collection.push('Collection is required');
        isValid = false;
      }

      // Source name validation
      if (!this.formData.sourceName.trim()) {
        this.errors.sourceName.push('Source name is required');
        isValid = false;
      } else if (this.formData.sourceName.trim().length < 2) {
        this.errors.sourceName.push('Source name must be at least 2 characters');
        isValid = false;
      }

      // Description validation
      if (this.formData.description.length > 300) {
        this.errors.description.push('Description cannot exceed 300 characters');
        isValid = false;
      }

      // Source type specific validation
      if (this.formData.sourceType === 'web') {
        if (!this.formData.url.trim() && this.urlList.length === 0) {
          this.errors.url.push('At least one URL is required for web sources');
          isValid = false;
        } else if (this.formData.url.trim() && !this.isValidUrl(this.formData.url)) {
          this.errors.url.push('Please enter a valid URL');
          isValid = false;
        }
      } else if (this.formData.sourceType === 'document') {
        if (this.uploadedFiles.length === 0) {
          this.errors.files.push('At least one document is required');
          isValid = false;
        }
      }

      return isValid;
    },

    isValidUrl(url) {
      try {
        new URL(url);
        return true;
      } catch {
        return false;
      }
    },

    goBack() {
      this.$router.go(-1);
    },

    cancel() {
      this.$router.push({ name: 'RAG' });
    },

    async createSource() {
      if (!this.validateForm()) {
        return;
      }

      this.loading = true;

      try {
        const formData = new FormData();

        // Required fields
        formData.append('name', this.formData.sourceName.trim());
        formData.append('collection_id', this.formData.collection);
        formData.append('source_type', this.formData.sourceType);

        // Optional description
        if (this.formData.description.trim()) {
          formData.append('description', this.formData.description.trim());
        }

        // Source type specific data
        if (this.formData.sourceType === 'web') {
          // Collect all URLs (main URL + additional URLs from list)
          const allUrls = [];

          if (this.formData.url.trim()) {
            allUrls.push(this.formData.url.trim());
          }

          this.urlList.forEach((urlItem) => {
            allUrls.push(urlItem.url);
          });

          // Add URLs array (swagger expects 'urls' parameter)
          allUrls.forEach((url) => {
            formData.append('urls', url);
          });

          if (this.formData.crawlDepth) {
            formData.append('crawl_depth', this.formData.crawlDepth.toString());
          }
        } else if (this.formData.sourceType === 'document') {
          // Add uploaded files (swagger expects 'files' parameter)
          this.uploadedFiles.forEach((file) => {
            formData.append('files', file);
          });
        }

        const response = await RagService.createRag(formData);

        // Success notification
        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          title: 'Success',
          message: 'Source created successfully',
        });

        this.$router.push({ name: 'RAG' });
      } catch (error) {
        let errorMessage = 'Failed to create source';

        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }

        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: errorMessage,
        });
      } finally {
        this.loading = false;
      }
    },

    addUrl() {
      if (this.formData.url.trim() && this.isValidUrl(this.formData.url)) {
        // Add URL to the list
        this.urlList.push({
          id: Date.now(),
          url: this.formData.url.trim(),
        });

        // Clear the input field
        this.formData.url = '';

        // Clear any URL errors
        this.errors.url = [];

        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          title: 'Success',
          message: 'URL added successfully',
        });
      } else {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: 'Please enter a valid URL',
        });
      }
    },

    removeUrl(index) {
      this.urlList.splice(index, 1);
      this.$store.dispatch('ui/showNotification', {
        type: 'success',
        title: 'Success',
        message: 'URL removed successfully',
      });
    },
    triggerFileUpload() {
      this.$refs.fileInput.click();
    },

    handleFileSelect(event) {
      const files = Array.from(event.target.files);
      this.processFiles(files);
    },

    handleFileDrop(event) {
      event.preventDefault();
      const files = Array.from(event.dataTransfer.files);
      this.processFiles(files);
    },

    handleDragOver(event) {
      event.preventDefault();
    },

    processFiles(files) {
      // Filter accepted file types
      const acceptedTypes = ['.txt', '.pdf', '.html', '.doc', '.docx', '.epub', '.md'];
      const validFiles = files.filter((file) => {
        const extension = '.' + file.name.split('.').pop().toLowerCase();
        return acceptedTypes.includes(extension);
      });

      if (validFiles.length !== files.length) {
        this.$store.dispatch('ui/showNotification', {
          type: 'warning',
          title: 'Invalid Files',
          message: 'Some files were not accepted. Only txt, pdf, html, doc, docx, epub, md files are allowed.',
        });
      }

      // Add valid files to uploaded files list
      this.uploadedFiles = [...this.uploadedFiles, ...validFiles];

      // Clear any previous file errors
      this.errors.files = [];

      if (validFiles.length > 0) {
        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          title: 'Files Added',
          message: `${validFiles.length} file(s) added successfully`,
        });
      }
    },

    removeFile(index) {
      this.uploadedFiles.splice(index, 1);
    },
  },
};
</script>

<style lang="scss" scoped>
.main-content {
  overflow: hidden;
}

.left-panel,
.right-panel {
  flex: 1;
  border-right: 1px solid $light-border;
  overflow-y: auto;

  .form-content {
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 24px;
  }
}

.right-panel {
  flex: 1;
  background-color: $light-surface-2;
  // Preview content will be added here
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;

  .form-label {
    font-family: $body-font-family;
    font-size: $body-3-size;
    font-weight: 600;
    color: $light-foreground-1;

    &.required {
      display: flex;
      align-items: center;
      gap: 4px;

      .required-asterisk {
        color: #EB575D;
        font-weight: 700;
      }
    }
  }

  .field-description {
    margin: 0;
    font-family: $body-font-family;
    font-size: $body-3-size;
    font-weight: 400;
    color: $light-foreground-3;
    line-height: 1.6;
  }
}

.helper-text {
  font-family: $body-font-family;
  font-size: $caption-1-size;
  font-weight: 400;
  color: $light-foreground-3;
  text-align: right;

  &.error-text {
    color: #f44336;
  }
}

.source-type-toggle {
  display: flex;
  background-color: $light-surface-3;
  border-radius: 12px;
  padding: 4px;
  height: 32px;

  .toggle-btn {
    flex: 1;
    height: 24px;
    border-radius: 8px;
    font-family: $body-font-family;
    font-size: $body-3-size;
    font-weight: 600;
    color: $light-foreground-4;
    background-color: transparent;
    text-transform: none;
    box-shadow: none;

    &.active {
      background-color: $light-surface-2;
      color: $light-foreground-1;
    }

    &:hover {
      background-color: rgba(255, 255, 255, 0.5);
    }
  }
}

.slider-group {
  display: flex;
  align-items: center;
  gap: 12px;

  .crawl-slider {
    flex: 1;

    :deep(.v-slider-track__fill) {
      background-color: $light-surface-3;
    }

    :deep(.v-slider-thumb__surface) {
      background-color: $light-surface-2;
      border: 2px solid $light-foreground-3;
    }
  }

  .slider-value {
    width: 48px;
    height: 32px;
    background-color: $light-surface-2;
    border: 1px solid $light-border;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: $body-font-family;
    font-size: $body-3-size;
    font-weight: 400;
    color: $light-foreground-4;
    flex-shrink: 0;
  }
}

.url-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.add-url-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background-color: $light-surface-2;
  border: 1px dashed $light-border;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: $light-surface-1;
    border-color: $light-primary;
  }

  .add-url-text {
    font-family: $body-font-family;
    font-size: $body-3-size;
    font-weight: 600;
    color: $light-foreground-1;
  }
}

.url-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;
}

.url-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background-color: $light-surface-2;
  border: 1px solid $light-border;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background-color: $light-surface-3;
  }

  .url-content {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;

    .url-text {
      font-family: $body-font-family;
      font-size: $body-3-size;
      font-weight: 600;
      color: $light-foreground-1;
      word-break: break-all;
    }
  }

  .remove-url-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background-color: $light-surface-1;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background-color: #fee;

      :deep(.icon) {
        color: #f44336 !important;
      }
    }
  }
}

.footer-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 12px 24px;
  border-top: 1px solid $light-border;
  background-color: $light-surface-2;

  .cancel-btn,
  .create-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 32px;
    font-family: $body-font-family;
    font-size: $body-3-size;
    font-weight: 600;
    text-transform: none;
  }

  .cancel-btn {
    background-color: $light-surface-1;
    color: $light-foreground-1;

    &:hover {
      background-color: $light-surface-3;
    }
  }

  .create-btn {
    width: 100px;
    background-color: $light-primary;
    color: $light-surface-2;

    &:hover {
      background-color: darken($light-primary, 10%);
    }
  }
}

// Vuetify overrides
.mank-input,
.mank-textarea,
.mank-select {
  :deep(.v-field__field) {
    background-color: $light-surface-1;
  }

  :deep(.v-field__input) {
    color: $light-foreground-1;
    font-family: $body-font-family;
    font-size: $body-3-size;
    font-weight: 400;
  }

  :deep(.v-field__outline) {
    --v-field-border-opacity: 0;
  }
}

.collection-select {
  :deep(.v-select__selection-text) {
    color: $light-foreground-4;
  }
}

.description-textarea {
  :deep(.v-field__field) {
    min-height: 120px;
  }
}

// Document Upload Area
.document-upload-area {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.drag-drop-area {
  background-color: $light-surface-1;
  border: 2px dashed $light-border;
  border-radius: 12px;
  padding: 12px;
  width: 100%;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: $light-primary;
    background-color: rgba(235, 87, 93, 0.02);
  }

  .upload-icon {
    .icon-circle {
      width: 48px;
      height: 48px;
      background-color: $light-surface-2;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
    }
  }

  .upload-text {
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 4px;

    .upload-title {
      margin: 0;
      font-family: $body-font-family;
      font-size: $body-2-size;
      font-weight: 700;
      color: $light-foreground-1;
      line-height: 1.4;
    }

    .upload-description {
      margin: 0;
      font-family: $body-font-family;
      font-size: $caption-1-size;
      font-weight: 400;
      color: $light-foreground-3;
      line-height: 1.6;
    }
  }

}
.upload-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  font-family: $body-font-family;
  font-size: $body-3-size;
  font-weight: 600;
  text-transform: none;
  border:1px solid $light-primary;
  color: $light-primary;
  &:hover {
    background-color: rgba(235, 87, 93, 0.1);
  }
}
</style>
